import axios from 'axios';

// Prefer relative '/api' in production (Firebase Hosting rewrites),
// fall back to localhost in dev if no explicit API URL provided.
const API_BASE_URL = process.env.REACT_APP_API_URL || (typeof window !== 'undefined' && window.location.hostname === 'localhost' ? 'http://localhost:5000/api' : '/api');

// Create axios instance
const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add auth token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle auth errors and subscription status
api.interceptors.response.use(
  (response) => response,
  (error) => {
    // Don't redirect for public routes (shared chat, public shop, etc.)
    const isPublicRoute = error.config?.url?.includes('/public/');
    
    // Handle subscription expired (402)
    if (error.response?.status === 402 && !isPublicRoute) {
      window.dispatchEvent(new CustomEvent('subscription-expired', {
        detail: { message: error.response?.data?.message || 'Subscription expired' }
      }));
    }
    
    // Handle unauthorized (401)
    if (error.response?.status === 401 && !isPublicRoute) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Auth API
export const authAPI = {
  login: (credentials) => api.post('/auth/login', credentials),
  register: (userData) => api.post('/auth/register', userData),
  verifyRegistration: (email, otp) => api.post('/auth/register/verify', { email, otp }),
  resendVerification: (email) => api.post('/auth/register/resend', { email }),
  forgotPassword: (email) => api.post('/auth/password/forgot', { email }),
  resendResetCode: (email) => api.post('/auth/password/forgot/resend', { email }),
  resetPassword: (email, otp, newPassword) => api.post('/auth/password/reset', { email, otp, newPassword }),
  uploadAvatar: (file) => {
    const form = new FormData();
    form.append('image', file);
    return api.post('/auth/avatar', form, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
  getProfile: () => api.get('/auth/profile'),
  updateProfile: (userData) => api.put('/auth/profile', userData),
};

// Farm API
export const farmAPI = {
  getFarms: () => api.get('/farm'),
  getFarm: (farmId) => api.get(`/farm/${farmId}`),
  createFarm: (farmData) => api.post('/farm', farmData),
  updateFarm: (farmId, farmData) => api.put(`/farm/${farmId}`, farmData),
  deleteFarm: (farmId) => api.delete(`/farm/${farmId}`),
  setFarmStatus: (farmId, isActive) => api.post(`/farm/${farmId}/status`, { isActive }),
  getFarmStats: (farmId) => api.get(`/farm/${farmId}/stats`),
  listMembers: (farmId) => api.get(`/farm/${farmId}/members`),
  updateMemberRole: (farmId, memberId, role) => api.put(`/farm/${farmId}/members/${memberId}`, { role }),
  removeMember: (farmId, memberId) => api.delete(`/farm/${farmId}/members/${memberId}`),
  listCohorts: (farmId) => api.get(`/farm/${farmId}/cohorts`),
  createCohort: (farmId, data) => api.post(`/farm/${farmId}/cohorts`, data),
  updateCohort: (farmId, cohortId, data) => api.put(`/farm/${farmId}/cohorts/${cohortId}`, data),
  deleteCohort: (farmId, cohortId) => api.delete(`/farm/${farmId}/cohorts/${cohortId}`),
};

// Activities API
export const activitiesAPI = {
  getActivities: (farmId, params = {}) => api.get(`/activities/farm/${farmId}`, { params }),
  getUpcomingActivities: (farmId, days = 7) => api.get(`/activities/farm/${farmId}/upcoming`, { params: { days } }),
  getActivity: (activityId) => api.get(`/activities/${activityId}`),
  createActivity: (farmId, activityData) => api.post(`/activities/farm/${farmId}`, activityData),
  updateActivity: (activityId, activityData) => api.put(`/activities/${activityId}`, activityData),
  toggleChecklistItem: (activityId, itemId, done) => api.patch(`/activities/${activityId}/checklist`, { itemId, done }),
  completeActivity: (activityId, notes) => api.patch(`/activities/${activityId}/complete`, { notes }),
  deleteActivity: (activityId) => api.delete(`/activities/${activityId}`),
};

// Hatching API
export const hatchingAPI = {
  listBatches: (farmId) => api.get(`/hatching/farm/${farmId}/batches`),
  createBatch: (farmId, data) => api.post(`/hatching/farm/${farmId}/batches`, data),
  getBatch: (id) => api.get(`/hatching/batches/${id}`),
  updateBatch: (id, data) => api.put(`/hatching/batches/${id}`, data),
  deleteBatch: (id) => api.delete(`/hatching/batches/${id}`),
  recordCandling: (id, data) => api.post(`/hatching/batches/${id}/candling`, data),
  lockdown: (id) => api.post(`/hatching/batches/${id}/lockdown`),
  recordHatch: (id, data) => api.post(`/hatching/batches/${id}/hatch`, data),
  createCohort: (id, data) => api.post(`/hatching/batches/${id}/create-cohort`, data)
};

// Records API
export const recordsAPI = {
  getRecords: (farmId, params = {}) => api.get(`/records/farm/${farmId}`, { params }),
  getRecordsByType: (farmId, type, params = {}) => api.get(`/records/farm/${farmId}/type/${type}`, { params }),
  getRecord: (recordId) => api.get(`/records/${recordId}`),
  createRecord: (farmId, recordData) => api.post(`/records/farm/${farmId}`, recordData),
  updateRecord: (recordId, recordData) => api.put(`/records/${recordId}`, recordData),
  deleteRecord: (recordId) => api.delete(`/records/${recordId}`),
  getAnalytics: (farmId, params = {}) => api.get(`/records/farm/${farmId}/analytics`, { params }),
  getStockLevels: (farmId) => api.get(`/records/farm/${farmId}/stock`),
  getBirds: (farmId, params = {}) => api.get(`/records/farm/${farmId}/birds`, { params }),
  getEggStock: (farmId, params = {}) => api.get(`/records/farm/${farmId}/eggs/stock`, { params }),
  adjustEggStock: (farmId, data) => api.post(`/records/farm/${farmId}/eggs/adjust`, data),
  exportRecords: (farmId, params = {}) => api.get(`/records/farm/${farmId}/export`, { params, responseType: 'blob' }),
};

// AI API
export const aiAPI = {
  // Chat with optional image
  chat: (data) => api.post('/ai/chat', data),
  // data: { message, conversationId?, farmId?, cohortId?, imageBase64?, context? }
  
  getRecommendations: (farmId) => api.get(`/ai/recommendations/${farmId}`),
  
  // Conversations
  listConversations: () => api.get('/ai/conversations'),
  createConversation: (data) => api.post('/ai/conversations', data),
  getConversation: (id) => api.get(`/ai/conversations/${id}`),
  updateConversation: (id, data) => api.put(`/ai/conversations/${id}`, data),
  deleteConversation: (id) => api.delete(`/ai/conversations/${id}`),
  shareConversation: (id) => api.post(`/ai/conversations/${id}/share`),
  
  // Public shared conversation (no auth)
  getSharedConversation: (shareToken) => api.get(`/public/ai/chat/${shareToken}`)
};

// Notifications API
export const notificationsAPI = {
  list: (params = {}) => api.get('/notifications', { params }),
  markRead: (id) => api.patch(`/notifications/${id}/read`),
  delete: (id) => api.delete(`/notifications/${id}`),
  deleteRead: () => api.delete('/notifications/read/all'),
};

// Billing API
export const billingAPI = {
  getStatus: () => api.get('/billing/status'),
  submitManual: (reference) => api.post('/billing/manual', { reference }),
  initiateStk: (phone) => api.post('/billing/mpesa/stk', { phone })
};

// Market API (includes supplier marketplace for farmers)
export const marketAPI = {
  // Legacy admin-managed products
  listProducts: (params = {}) => api.get('/market/products', { params }),
  createProduct: (data) => api.post('/market/admin/products', data),
  updateProduct: (id, data) => api.put(`/market/admin/products/${id}`, data),
  deleteProduct: (id) => api.delete(`/market/admin/products/${id}`),
  uploadImage: (file) => {
    const form = new FormData();
    form.append('image', file);
    return api.post('/market/admin/upload', form, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
  orderProduct: (data) => api.post('/market/order', data),
  listOrders: (params = {}) => api.get('/market/orders', { params }),
  getOrder: (id) => api.get(`/market/orders/${id}`),
  cancelOrder: (id) => api.post(`/market/orders/${id}/cancel`),
  adminListOrders: (params = {}) => api.get('/market/admin/orders', { params }),
  adminGetOrder: (id) => api.get(`/market/admin/orders/${id}`),
  adminUpdateOrderStatus: (id, status) => api.put(`/market/admin/orders/${id}/status`, { status }),
  
  // Supplier marketplace (farmers buying from suppliers)
  listSuppliers: (params = {}) => api.get('/market/suppliers', { params }),
  getSupplier: (supplierId) => api.get(`/market/suppliers/${supplierId}`),
  getSupplierProducts: (supplierId, params = {}) => api.get(`/market/suppliers/${supplierId}/products`, { params }),
  listSupplierProducts: (params = {}) => api.get('/market/supplier-products', { params }),
  placeSupplierOrder: (data) => api.post('/market/supplier-orders', data),
  getMySupplierOrders: (params = {}) => api.get('/market/my-supplier-orders', { params }),
  getMySupplierOrder: (orderId) => api.get(`/market/my-supplier-orders/${orderId}`),
  
  // Payment for supplier orders
  checkWalletPayment: (orderId) => api.get(`/market/my-supplier-orders/${orderId}/wallet-check`),
  payWithWallet: (orderId) => api.post(`/market/my-supplier-orders/${orderId}/pay-wallet`),
  payWithMpesa: (orderId, phone) => api.post(`/market/my-supplier-orders/${orderId}/pay-mpesa`, { phone }),
  checkPaymentStatus: (orderId) => api.get(`/market/my-supplier-orders/${orderId}/payment-status`),
  getEscrowStatus: (orderId) => api.get(`/market/my-supplier-orders/${orderId}/escrow`),
  confirmDelivery: (orderId) => api.post(`/market/my-supplier-orders/${orderId}/confirm-delivery`)
};

// Supplier API (for supplier users)
export const supplierAPI = {
  // Profile
  getProfile: () => api.get('/supplier/profile'),
  updateProfile: (data) => api.put('/supplier/profile', data),
  uploadLogo: (file) => {
    const form = new FormData();
    form.append('image', file);
    return api.post('/supplier/profile/logo', form, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
  
  // Products
  listProducts: (params = {}) => api.get('/supplier/products', { params }),
  createProduct: (data) => api.post('/supplier/products', data),
  updateProduct: (productId, data) => api.put(`/supplier/products/${productId}`, data),
  deleteProduct: (productId) => api.delete(`/supplier/products/${productId}`),
  uploadProductImage: (productId, file) => {
    const form = new FormData();
    form.append('image', file);
    return api.post(`/supplier/products/${productId}/image`, form, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
  
  // Orders (received from farmers)
  listOrders: (params = {}) => api.get('/supplier/orders', { params }),
  getOrder: (orderId) => api.get(`/supplier/orders/${orderId}`),
  updateOrderStatus: (orderId, status, note) => api.put(`/supplier/orders/${orderId}/status`, { status, note }),
  
  // Dashboard stats
  getStats: () => api.get('/supplier/stats')
};

// Client API (for client users buying from farmers)
export const clientAPI = {
  // Profile
  getProfile: () => api.get('/client/profile'),
  updateProfile: (data) => api.put('/client/profile', data),
  
  // Browse farms/shops
  listShops: (params = {}) => api.get('/client/shops', { params }),
  getShop: (slug) => api.get(`/client/shops/${slug}`),
  listFarmProducts: (params = {}) => api.get('/client/products', { params }),
  
  // Orders
  listOrders: (params = {}) => api.get('/client/orders', { params }),
  getOrder: (orderId) => api.get(`/client/orders/${orderId}`),
  
  // Order Payments
  initiatePayment: (orderId, phone) => api.post(`/client/orders/${orderId}/pay-mpesa`, { phone }),
  checkPaymentStatus: (orderId) => api.get(`/client/orders/${orderId}/payment-status`),
  checkWalletPayment: (orderId) => api.get(`/client/orders/${orderId}/wallet-check`),
  payWithWallet: (orderId) => api.post(`/client/orders/${orderId}/pay-wallet`),
  
  // Delivery Confirmation
  confirmDelivery: (orderId) => api.post(`/client/orders/${orderId}/confirm-delivery`),
  
  // Favorites
  getFavorites: () => api.get('/client/favorites'),
  addFavorite: (farmId) => api.post(`/client/favorites/${farmId}`),
  removeFavorite: (farmId) => api.delete(`/client/favorites/${farmId}`),
  
  // Dashboard stats
  getStats: () => api.get('/client/stats')
};

// Dashboard API
export const dashboardAPI = {
  getOverview: (farmId, params = {}) => api.get(`/dashboard/farm/${farmId}/overview`, { params }),
  getBirds: (farmId, params = {}) => api.get(`/records/farm/${farmId}/birds`, { params }),
};

// Feedback API
export const feedbackAPI = {
  submit: (data) => api.post('/feedback', data),
  adminList: (params = {}) => api.get('/admin/feedback', { params })
};

export default api;
// Admin API
export const adminAPI = {
  getUsers: (params = {}) => api.get('/admin/users', { params }),
  getSubsSummary: (params={}) => api.get('/admin/subscriptions/summary', { params }),
  activateSubscription: (userId, days=30) => api.post('/admin/subscriptions/activate', { userId, days }),
  extendSubscriptionFree: (userId, days=30) => api.post('/admin/subscriptions/extend-free', { userId, days }),
  
  // Supplier management
  getSuppliers: (params = {}) => api.get('/admin/suppliers', { params }),
  getSuppliersSummary: () => api.get('/admin/suppliers/summary'),
  verifySupplier: (supplierId) => api.put(`/admin/suppliers/${supplierId}/verify`),
  rejectSupplier: (supplierId, reason) => api.put(`/admin/suppliers/${supplierId}/reject`, { reason }),
  toggleSupplierActive: (supplierId) => api.put(`/admin/suppliers/${supplierId}/toggle-active`),
  
  // Supplier products management
  getSupplierProducts: (params = {}) => api.get('/admin/supplier-products', { params }),
  getSupplierProductsSummary: () => api.get('/admin/supplier-products/summary'),
  approveProduct: (productId) => api.put(`/admin/supplier-products/${productId}/approve`),
  rejectProduct: (productId, reason) => api.put(`/admin/supplier-products/${productId}/reject`, { reason }),
  bulkApproveProducts: (productIds) => api.post('/admin/supplier-products/bulk-approve', { productIds }),
  deleteSupplierProduct: (productId) => api.delete(`/admin/supplier-products/${productId}`),
  
  // Dashboard charts
  getRevenueOrdersChart: () => api.get('/admin/charts/revenue-orders'),
  getUserDistributionChart: () => api.get('/admin/charts/user-distribution'),
  getSubscriptionStatusChart: () => api.get('/admin/charts/subscription-status'),
  getUserGrowthChart: () => api.get('/admin/charts/user-growth'),
};

// Invites API
export const invitesAPI = {
  create: (farmId, data) => api.post(`/invites/farm/${farmId}`, data),
  preview: (token) => api.get(`/invites/${token}`),
  accept: (token) => api.post(`/invites/${token}/accept`),
  cancel: (inviteId) => api.delete(`/invites/${inviteId}`)
};

// Shop API (Farmer management)
export const shopAPI = {
  // Shop settings
  getSettings: (farmId) => api.get(`/shop/farm/${farmId}/settings`),
  updateSettings: (farmId, data) => api.put(`/shop/farm/${farmId}/settings`, data),
  uploadShopImage: (farmId, file) => {
    const form = new FormData();
    form.append('image', file);
    return api.post(`/shop/farm/${farmId}/settings/image`, form, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
  // Products
  listProducts: (farmId) => api.get(`/shop/farm/${farmId}/products`),
  createProduct: (farmId, data) => api.post(`/shop/farm/${farmId}/products`, data),
  updateProduct: (farmId, productId, data) => api.put(`/shop/farm/${farmId}/products/${productId}`, data),
  deleteProduct: (farmId, productId) => api.delete(`/shop/farm/${farmId}/products/${productId}`),
  // Image upload
  uploadProductImage: (farmId, file) => {
    const form = new FormData();
    form.append('image', file);
    return api.post(`/shop/farm/${farmId}/upload-image`, form, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
  // Orders
  listOrders: (farmId, params = {}) => api.get(`/shop/farm/${farmId}/orders`, { params }),
  getOrder: (farmId, orderId) => api.get(`/shop/farm/${farmId}/orders/${orderId}`),
  updateOrderStatus: (farmId, orderId, status) => api.put(`/shop/farm/${farmId}/orders/${orderId}/status`, { status }),
  updatePaymentStatus: (farmId, orderId, data) => api.put(`/shop/farm/${farmId}/orders/${orderId}/payment`, data),
  cancelOrder: (farmId, orderId, reason) => api.put(`/shop/farm/${farmId}/orders/${orderId}/cancel`, { reason }),
  deleteOrder: (farmId, orderId) => api.delete(`/shop/farm/${farmId}/orders/${orderId}`),
  // Earnings & Wallet
  getEarnings: (farmId) => api.get(`/shop/farm/${farmId}/earnings`),
  getFarmWallet: (farmId) => api.get(`/shop/farm/${farmId}/wallet`),
  // Customers
  listCustomers: (farmId) => api.get(`/shop/farm/${farmId}/customers`)
};

// Public Shop API (No auth required for customers)
export const publicShopAPI = {
  getShop: (slug) => api.get(`/public/shop/${slug}`),
  placeOrder: (slug, data) => api.post(`/public/shop/${slug}/order`, data),
  checkOrderStatus: (slug, orderNumber, phone) => api.get(`/public/shop/${slug}/order/${orderNumber}`, { params: { phone } }),
  // M-Pesa STK Push payment
  initiatePayment: (slug, orderId, phone) => api.post(`/public/shop/${slug}/order/${orderId}/pay`, { phone }),
  checkPaymentStatus: (slug, orderId) => api.get(`/public/shop/${slug}/order/${orderId}/payment-status`)
};

// Public Academy API
export const academyAPI = {
  listCourses: () => api.get('/public/academy/courses'),
  getCourse: (slug) => api.get(`/public/academy/courses/${slug}`)
};

// Admin Academy API
export const adminAcademyAPI = {
  listCourses: () => api.get('/admin/academy/courses'),
  createCourse: (data) => api.post('/admin/academy/courses', data),
  updateCourse: (id, data) => api.put(`/admin/academy/courses/${id}`, data),
  deleteCourse: (id) => api.delete(`/admin/academy/courses/${id}`),
  createLesson: (courseId, data) => api.post(`/admin/academy/courses/${courseId}/lessons`, data),
  updateLesson: (id, data) => api.put(`/admin/academy/lessons/${id}`, data),
  deleteLesson: (id) => api.delete(`/admin/academy/lessons/${id}`),
  uploadFile: (file) => {
    const form = new FormData();
    form.append('file', file);
    return api.post('/admin/academy/upload', form, { headers: { 'Content-Type': 'multipart/form-data' } });
  }
};

// Admin Settlements API
export const adminSettlementsAPI = {
  // Farm settlements
  getPending: () => api.get('/admin/settlements/pending'),
  getPendingOrders: (farmId) => api.get(`/admin/settlements/farm/${farmId}/pending-orders`),
  processSettlement: (farmId, data) => api.post(`/admin/settlements/farm/${farmId}/settle`, data),
  getHistory: (params = {}) => api.get('/admin/settlements/history', { params }),
  exportPending: () => api.get('/admin/settlements/export', { responseType: 'blob' }),
  getOmabraEarnings: (params = {}) => api.get('/admin/settlements/omabra-earnings', { params }),
  updatePlatformFee: (newFeePercent = 1.0) => api.post('/admin/settlements/update-platform-fee', { newFeePercent }),
  
  // Supplier settlements
  getSuppliersPending: () => api.get('/admin/settlements/suppliers/pending'),
  getSuppliersEarnings: (params = {}) => api.get('/admin/settlements/suppliers/earnings', { params }),
  getSupplierPendingOrders: (supplierId) => api.get(`/admin/settlements/suppliers/${supplierId}/pending-orders`),
  processSupplierSettlement: (supplierId, data) => api.post(`/admin/settlements/suppliers/${supplierId}/settle`, data),
  exportSuppliersPending: () => api.get('/admin/settlements/suppliers/export', { responseType: 'blob' })
};

// Reports API
export const reportsAPI = {
  // Get monthly report data preview
  getMonthlyReport: (farmId, params = {}) => api.get(`/reports/farm/${farmId}/monthly`, { params }),
  // Download PDF report
  downloadMonthlyPdf: (farmId, params = {}) => api.get(`/reports/farm/${farmId}/monthly/pdf`, { 
    params, 
    responseType: 'blob' 
  }),
  // Send report via email
  sendMonthlyReport: (farmId, data = {}) => api.post(`/reports/farm/${farmId}/monthly/send`, data),
  // Get available months for reports
  getAvailableMonths: (farmId) => api.get(`/reports/farm/${farmId}/available`)
};

// Wallet API
export const walletAPI = {
  // Get wallet balance and summary
  getWallet: () => api.get('/wallet'),
  // Get transaction history
  getTransactions: (params = {}) => api.get('/wallet/transactions', { params }),
  // Initiate M-Pesa STK Push for wallet top-up
  initiateTopUp: (data) => api.post('/wallet/initiate-top-up', data),
  // Check top-up status
  checkTopUpStatus: (transactionId) => api.get(`/wallet/top-up-status/${transactionId}`),
  // Top-up wallet (record M-Pesa payment - legacy/manual)
  topUp: (data) => api.post('/wallet/top-up', data),
  // Request withdrawal
  requestWithdrawal: (data) => api.post('/wallet/withdraw', data),
  // Get withdrawal requests
  getWithdrawals: (params = {}) => api.get('/wallet/withdrawals', { params }),
  // Request cancellation of withdrawal (requires admin approval)
  requestCancelWithdrawal: (id, data = {}) => api.put(`/wallet/withdrawals/${id}/request-cancel`, data),
  // Get escrows
  getEscrows: (params = {}) => api.get('/wallet/escrows', { params }),
  // Check if can pay order with wallet
  checkWalletPayment: (orderId) => api.get(`/market/my-supplier-orders/${orderId}/wallet-check`),
  // Pay for supplier order with wallet
  payWithWallet: (orderId) => api.post(`/market/my-supplier-orders/${orderId}/pay-wallet`),
  // Get order escrow status
  getOrderEscrow: (orderId) => api.get(`/market/my-supplier-orders/${orderId}/escrow`),
  // Admin endpoints
  adminGetAll: (params = {}) => api.get('/wallet/admin/all', { params }),
  adminGetWithdrawals: (params = {}) => api.get('/wallet/admin/withdrawals', { params }),
  adminCompleteWithdrawal: (id, data) => api.put(`/wallet/admin/withdrawals/${id}/complete`, data),
  adminRejectWithdrawal: (id, data) => api.put(`/wallet/admin/withdrawals/${id}/reject`, data),
  adminApproveCancelWithdrawal: (id) => api.put(`/wallet/admin/withdrawals/${id}/approve-cancel`),
  adminRejectCancelWithdrawal: (id, data) => api.put(`/wallet/admin/withdrawals/${id}/reject-cancel`, data),
  adminAdjustWallet: (data) => api.post('/wallet/admin/adjust', data),
  adminGetEscrows: (params = {}) => api.get('/wallet/admin/escrows', { params }),
  adminReleaseEscrow: (id, data = {}) => api.put(`/wallet/admin/escrows/${id}/release`, data),
  adminRefundEscrow: (id, data = {}) => api.put(`/wallet/admin/escrows/${id}/refund`, data)
};